// Формат времени — ядро продукта (docs/design/README.md §4). Всё числовое —
// mono-num на стороне компонентов; здесь только строки.
//
// Канон: момент едет RFC3339 в UTC и показывается в зоне устройства смотрящего
// (`momentZone()`), календарный день едет `YYYY-MM-DD` и зоны не имеет вовсе, а
// его границы и срок считаются в зоне организации (`APP_TZ`). Отсюда две ветки
// форматтеров — `formatDay*` для дней и `format*` для моментов; смешивать их
// нельзя: прогон дня через `new Date` уводит его на сутки западнее UTC.

import { ALL_DAY_DUE_TIME, APP_TZ, dayOf, momentOf, momentZone, parseDay, zoneInfo } from './zone'

const MIN = 60
const HOUR = 3600

export function pluralRu(n: number, forms: [string, string, string]): string {
  const abs = Math.abs(n)
  const n10 = abs % 10
  const n100 = abs % 100
  if (n10 === 1 && n100 !== 11) return forms[0]
  if (n10 >= 2 && n10 <= 4 && (n100 < 10 || n100 >= 20)) return forms[1]
  return forms[2]
}

// Длительность роллапа: компактно, не больше двух старших единиц — «2д 8ч»,
// «6ч 12м», «45м». Ноль → «0м».
export function formatDuration(seconds: number): string {
  const total = Math.max(0, Math.floor(seconds / MIN))
  const days = Math.floor(total / (24 * MIN))
  const hours = Math.floor((total % (24 * MIN)) / MIN)
  const mins = total % MIN
  if (days > 0) return hours > 0 ? `${days}д ${hours}ч` : `${days}д`
  if (hours > 0) return mins > 0 ? `${hours}ч ${mins}м` : `${hours}ч`
  return `${mins}м`
}

// Отчётная сумма за период: часы не сворачиваются в дни — «48ч 30м», «142ч»,
// «30м» (docs/design/screens/reports.md), в отличие от роллапа поддерева.
export function formatHoursDuration(seconds: number): string {
  const total = Math.max(0, Math.floor(seconds / MIN))
  const hours = Math.floor(total / MIN)
  const mins = total % MIN
  if (hours === 0) return `${mins}м`
  return mins > 0 ? `${hours}ч ${mins}м` : `${hours}ч`
}

// Обратный отсчёт помодоро-фазы: MM:SS, минуты не ограничены 59 (240м → «240:00»).
export function formatCountdown(seconds: number): string {
  const s = Math.max(0, Math.floor(seconds))
  const m = Math.floor(s / MIN)
  const ss = s % MIN
  return `${String(m).padStart(2, '0')}:${String(ss).padStart(2, '0')}`
}

// «Идёт сейчас»: HH:MM:SS с живым тиком. Часы растут за 24 (26:00:00).
export function formatTimer(seconds: number): string {
  const s = Math.max(0, Math.floor(seconds))
  const h = Math.floor(s / HOUR)
  const m = Math.floor((s % HOUR) / MIN)
  const ss = s % MIN
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${pad(h)}:${pad(m)}:${pad(ss)}`
}

// Момент срока: календарный день + время суток, собранные в зоне организации —
// явным аргументом, а не дефолтом. Срок — обязательство студии: «до 8 августа
// 17:00» значит один и тот же инстант для всех, кто на него смотрит, и зона
// устройства смотрящего его не двигает. Без времени берётся час студии
// (`ALL_DAY_DUE_TIME`) — тот же, которым срок «на весь день» считает бэкенд:
// иначе тон карточки расходился бы с алертом. Битый день (не `YYYY-MM-DD`) даёт
// Invalid Date — вызывающий обязан его отсеять, а не получить сдвинутый срок.
export function dueMoment(dueDate: string, dueTime?: string | null): Date {
  return (
    momentOf(dueDate, dueTime && dueTime !== '' ? dueTime : ALL_DAY_DUE_TIME, APP_TZ) ??
    new Date(NaN)
  )
}

// Момент срока в миллисекундах для сортировки: без срока и с битым днём — в
// конец списка, оба случая одинаково «неизвестно когда».
export function dueMomentMs(dueDate: string | null, dueTime?: string | null): number {
  if (dueDate === null) return Number.POSITIVE_INFINITY
  const ms = dueMoment(dueDate, dueTime).getTime()
  return Number.isNaN(ms) ? Number.POSITIVE_INFINITY : ms
}

export type DueStatus = 'overdue' | 'today' | 'future'

// Статус срока — по моменту, а не по суткам: задача на 09:00 к полудню уже
// просрочена, а «на весь день» держится до часа студии. Сутки («сегодня»)
// считаются в зоне организации, а не в зоне браузера.
export function dueStatus(
  dueDate: string,
  now: Date = new Date(),
  dueTime?: string | null,
): DueStatus {
  const moment = dueMoment(dueDate, dueTime)
  const ms = moment.getTime()
  if (Number.isNaN(ms)) return 'future'
  if (ms < now.getTime()) return 'overdue'
  return dayOf(moment, APP_TZ) === dayOf(now, APP_TZ) ? 'today' : 'future'
}

// Форматтеры момента заводятся на вызов и кешируются по зоне, а не живут
// модульными константами: зона момента — зона устройства, и прибитая к модулю
// константа не переживает ни перелёт с открытой вкладкой, ни подмену зоны в
// тесте. Ключ — имя форматтера плюс зона: наборы опций у них разные.
const momentFmtCache = new Map<string, Intl.DateTimeFormat>()

function momentFmt(key: string, tz: string, options: Intl.DateTimeFormatOptions) {
  const cacheKey = `${key}|${tz}`
  const cached = momentFmtCache.get(cacheKey)
  if (cached) return cached
  const fmt = new Intl.DateTimeFormat('ru-RU', { timeZone: tz, ...options })
  momentFmtCache.set(cacheKey, fmt)
  return fmt
}

const CLOCK_OPTS: Intl.DateTimeFormatOptions = { hour: '2-digit', minute: '2-digit' }
const LONG_DATE_OPTS: Intl.DateTimeFormatOptions = {
  day: 'numeric',
  month: 'long',
  year: 'numeric',
}
const SHORT_DATE_OPTS: Intl.DateTimeFormatOptions = {
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
}

// Часы:минуты события — отсечка idle-предложения «Обрезать до 18:40».
export function formatClock(iso: string, tz: string = momentZone()): string {
  return momentFmt('clock', tz, CLOCK_OPTS).format(new Date(iso))
}

// Абсолют для тултипа.
export function formatAbsolute(iso: string, withTime = false, tz: string = momentZone()): string {
  const d = new Date(iso)
  return withTime
    ? momentFmt('dateTime', tz, { ...LONG_DATE_OPTS, ...CLOCK_OPTS }).format(d)
    : momentFmt('date', tz, LONG_DATE_OPTS).format(d)
}

// Дата события в строке списка: «29.07.2026». Числовой формат, а не «29 июля
// 2026 г.» — в плотных рядах и на карточках место считанное.
export function formatDate(iso: string, tz: string = momentZone()): string {
  return momentFmt('shortDate', tz, SHORT_DATE_OPTS).format(new Date(iso))
}

// Дата и время события: «29.07.2026 16:40». Точная отметка вместо «3ч назад» —
// по ней видно, когда именно это было, без арифметики в голове.
//
// Дефолт — зона устройства. Момент, выведенный из срока (пинг напоминания,
// подпись на диаграмме), зовёт с явным `APP_TZ`: он живёт в кадре обязательства
// студии, и «срок 17:00» минус «за 15 мин» обязаны вычитаться на экране.
export function formatDateTime(iso: string, tz: string = momentZone()): string {
  const d = new Date(iso)
  return `${momentFmt('shortDate', tz, SHORT_DATE_OPTS).format(d)} ${momentFmt('clock', tz, CLOCK_OPTS).format(d)}`
}

const MONTHS_RU = [
  'января',
  'февраля',
  'марта',
  'апреля',
  'мая',
  'июня',
  'июля',
  'августа',
  'сентября',
  'октября',
  'ноября',
  'декабря',
]

// Календарный день `YYYY-MM-DD` → «29.07.2026». Через строку, а не через Date:
// у дня нет зоны, и любой прогон через момент увёл бы его на сутки.
export function formatDay(day: string): string {
  const d = parseDay(day)
  if (!d) return day
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${pad(d.date)}.${pad(d.month)}.${d.year}`
}

// Тот же день словами — для тултипа: «29 июля 2026 г.».
export function formatDayLong(day: string): string {
  const d = parseDay(day)
  if (!d) return day
  return `${d.date} ${MONTHS_RU[d.month - 1] ?? ''} ${d.year} г.`
}

// День без года — для строки предпросмотра под полем даты: год стоит в самом
// поле рядом, повторять его в подсказке незачем.
export function formatDayMonth(day: string): string {
  const d = parseDay(day)
  if (!d) return day
  return `${d.date} ${MONTHS_RU[d.month - 1] ?? ''}`
}

// Срок «на весь день» — не весь день: бэкенд материализует его часом студии, с
// этим моментом задача уезжает в календарь и по нему же приходит напоминание.
// Час берётся из рантайм-конфига (`ALL_DAY_DUE_TIME`), а не из литерала: смена
// часа в `.env` иначе развела бы обещание на экране с тем, что придёт человеку.
export function allDayDueNote(): string {
  return `весь день; в календаре и в напоминании — ${ALL_DAY_DUE_TIME} ${zoneInfo().short}`
}

// Во что превратится срок, который набирают прямо сейчас, — строка под полем
// даты. Человек ставит «до 8 августа», а в своём календаре видит момент; без
// этой строки он видит его впервые уже там.
export function dueCalendarPreview(dueDate: string, dueTime?: string | null): string | null {
  if (!parseDay(dueDate)) return null
  return dueTime && dueTime !== ''
    ? `в календаре: ${formatDayMonth(dueDate)}, ${dueTime} ${zoneInfo().short}`
    : allDayDueNote()
}
